import React, { useState, useEffect } from 'react'
import { unifiedAuthApi } from '../services/unified-auth-api'
import { useAuthStore } from '../stores/authStore'

interface UnifiedLoginProps {
  onLoginSuccess: (user: any) => void
}

export const UnifiedLogin: React.FC<UnifiedLoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('')
  const [step, setStep] = useState<'email' | 'waiting' | 'manual-input' | 'verify'>('email')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [manualToken, setManualToken] = useState('')
  
  // Get auth store methods
  const { verifyMagicLink } = useAuthStore()

  // Check for magic link token in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('token')
    
    console.log('🔍 UnifiedLogin useEffect: Checking for token in URL', {
      hasToken: !!token,
      tokenPreview: token?.substring(0, 10) + '...',
      currentStep: step,
      currentURL: window.location.href
    })
    
    if (token) {
      console.log('🔍 UnifiedLogin: Token found in URL, starting verification...')
      handleMagicLinkVerification(token)
    } else {
      console.log('🔍 UnifiedLogin: No token in URL')
    }
  }, [])

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      const result = await unifiedAuthApi.requestMagicLink(email)
      
      if (result.success) {
        setMessage(result.message || 'Magic link sent! Check your email.')
        setStep('waiting')
      } else {
        setError(result.error || 'Failed to send magic link')
      }
    } catch (err) {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleMagicLinkVerification = async (token: string) => {
    console.log('🔍 UnifiedLogin: handleMagicLinkVerification called', {
      tokenPreview: token.substring(0, 10) + '...',
      currentStep: step,
      isLoading: loading
    })
    
    setLoading(true)
    setError('')
    setStep('verify')

    try {
      console.log('🔍 UnifiedLogin: Calling authStore.verifyMagicLink...')
      const result = await verifyMagicLink(token)
      
      console.log('🔍 UnifiedLogin: Auth store verification result:', {
        success: result.success,
        hasUser: !!result.user,
        userEmail: result.user?.email
      })
      
      if (result.success && result.user) {
        console.log('✅ UnifiedLogin: Verification successful, auth store updated')
        setMessage('Admin login successful!')
        
        console.log('🔍 UnifiedLogin: About to call onLoginSuccess with user:', {
          id: result.user.id,
          email: result.user.email,
          role: result.user.role
        })
        
        onLoginSuccess(result.user)
        
        console.log('✅ UnifiedLogin: onLoginSuccess called, cleaning up URL')
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname)
        console.log('✅ UnifiedLogin: URL cleaned up')
      } else {
        console.error('❌ UnifiedLogin: Verification failed')
        setError('Magic link verification failed')
        // Don't reset to email if we're doing manual verification - stay in manual-input for retry
        if (step === 'verify') {
          setStep('manual-input')
        } else {
          setStep('email')
        }
      }
    } catch (err: any) {
      console.error('❌ UnifiedLogin: Exception during verification:', err)
      setError('Verification failed. Please try again.')
      // Don't reset to email if we're doing manual verification - stay in manual-input for retry
      if (step === 'verify') {
        setStep('manual-input')
      } else {
        setStep('email')
      }
    } finally {
      setLoading(false)
      console.log('🔍 UnifiedLogin: Verification process completed, loading set to false')
    }
  }

  const resetForm = () => {
    setStep('email')
    setEmail('')
    setError('')
    setMessage('')
    setManualToken('')
  }

  const handleManualTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!manualToken.trim()) {
      setError('Please enter a valid token')
      return
    }
    await handleMagicLinkVerification(manualToken.trim())
  }

  const switchToManualInput = () => {
    setStep('manual-input')
    setError('')
    setMessage('')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Virtual Revamp Admin
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Unified Magic Link Authentication
          </p>
        </div>

        {step === 'email' && (
          <form className="mt-8 space-y-6" onSubmit={handleEmailSubmit}>
            <div>
              <label htmlFor="email" className="sr-only">
                Admin Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Admin email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !email}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending Magic Link...
                  </div>
                ) : (
                  'Send Admin Magic Link'
                )}
              </button>
            </div>
          </form>
        )}

        {step === 'waiting' && (
          <div className="mt-8 space-y-6">
            <div className="text-center">
              <div className="animate-pulse">
                <div className="mx-auto h-12 w-12 text-indigo-600">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Check Your Email</h3>
              <p className="mt-2 text-sm text-gray-600">
                We've sent a magic link to <strong>{email}</strong>
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Click the link in your email to access the admin panel
              </p>
            </div>

            <div className="space-y-2">
              <button
                onClick={resetForm}
                className="w-full text-center text-sm text-indigo-600 hover:text-indigo-500"
              >
                Use a different email address
              </button>
              <button
                onClick={switchToManualInput}
                className="w-full text-center text-sm text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Enter token manually instead
              </button>
            </div>
          </div>
        )}

        {step === 'manual-input' && (
          <form className="mt-8 space-y-6" onSubmit={handleManualTokenSubmit}>
            <div>
              <label htmlFor="token" className="block text-sm font-medium text-gray-700 mb-2">
                Magic Link Token
              </label>
              <input
                id="token"
                name="token"
                type="text"
                required
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Paste your magic link token here"
                value={manualToken}
                onChange={(e) => setManualToken(e.target.value)}
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">
                Copy the token from the console output or extract it from the magic link URL
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !manualToken.trim()}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying Token...
                  </div>
                ) : (
                  'Verify Token'
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setStep('waiting')}
                className="text-sm text-gray-600 hover:text-gray-500"
              >
                ← Back to email verification
              </button>
            </div>
          </form>
        )}

        {step === 'verify' && (
          <div className="mt-8 space-y-6">
            <div className="text-center">
              <div className="animate-spin mx-auto h-12 w-12 text-indigo-600">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Verifying Access</h3>
              <p className="mt-2 text-sm text-gray-600">
                Validating your admin credentials...
              </p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {message && (
          <div className="rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Development Info */}
        <div className="mt-8 text-center">
          <div className="text-xs text-gray-500 space-y-1">
            <p>🔗 Using Unified Magic Link Authentication</p>
            <p>✅ Same system as main frontend</p>
            <p>🔒 Admin user type validation</p>
            <p>📧 Check console for development magic links</p>
            {step === 'waiting' && (
              <p className="text-indigo-600 font-medium">💡 Can't access email? Use "Enter token manually" below</p>
            )}
            {step === 'manual-input' && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-800">
                <p className="text-xs font-medium">🔧 Development Mode</p>
                <p className="text-xs">Copy token from console output above</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UnifiedLogin